"use client";
import React, { useState } from 'react';
import * as Icon from "@phosphor-icons/react/dist/ssr";
import { validatePromoCode, applyPromoCode, formatPromoCode, PromoCode } from '../../utils/promoUtils';

interface PromoCodeInputProps {
  cartTotal: number;
  shippingCost: number;
  onPromoCodeApplied: (discount: number, promoCode: PromoCode) => void;
  onPromoCodeRemoved: () => void;
  appliedPromoCode?: PromoCode;
  productIds?: number[];
  categories?: string[];
  userId?: number;
}

const PromoCodeInput: React.FC<PromoCodeInputProps> = ({
  cartTotal,
  shippingCost,
  onPromoCodeApplied,
  onPromoCodeRemoved,
  appliedPromoCode,
  productIds = [],
  categories = [],
  userId
}) => {
  const [promoCode, setPromoCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const validation = await validatePromoCode(
        promoCode.trim(),
        cartTotal,
        userId,
        productIds,
        categories
      );

      if (validation.isValid && validation.promoCode) {
        const result = applyPromoCode(validation.promoCode, cartTotal, shippingCost);
        onPromoCodeApplied(result.discount, validation.promoCode);
        setPromoCode('');
      } else {
        setError(validation.message || 'Code promo invalide.');
      }
    } catch (error) {
      console.error('Error applying promo code:', error);
      setError('Erreur lors de l\'application du code promo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
    onPromoCodeRemoved();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyPromoCode();
    }
  };

  return (
    <div className="promo-code-section p-0">
      {appliedPromoCode ? (
        <div className="applied-promo-code bg-green-50 border border-green-200 rounded-lg py-2 px-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon.CheckCircle className="text-green-600 flex-shrink-0" size={18} weight="duotone" />
            <span className="font-medium text-green-800 text-sm truncate">{appliedPromoCode.code}</span>
            <span className="text-green-600 text-xs hidden sm:inline">— {formatPromoCode(appliedPromoCode)}</span>
          </div>
          <button
            onClick={handleRemovePromoCode}
            className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded flex-shrink-0"
            title="Retirer le code promo"
          >
            <Icon.X size={16} />
          </button>
        </div>
      ) : (
        <div className="promo-code-input-section flex gap-2">
          <input
            type="text"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            onKeyPress={handleKeyPress}
            placeholder="Code promo"
            className="flex-1 min-w-0 border-2 border-gray-200 bg-white rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-pink focus:ring-0"
            disabled={isLoading}
          />
          <button
            onClick={handleApplyPromoCode}
            disabled={isLoading || !promoCode.trim()}
            className="bg-pink text-white px-3 py-1.5 rounded-lg border-0 hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 text-sm flex-shrink-0"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
            ) : (
              <>
                <Icon.Ticket size={14} />
                <span>Appliquer</span>
              </>
            )}
          </button>
        </div>
      )}

      {error && (
        <div className="error-message mt-1.5 py-1.5 px-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs flex items-center gap-1">
          <Icon.Warning size={12} />
          {error}
        </div>
      )}

      <p className="promo-info mt-1.5 text-[10px] text-gray-500">Un seul code par commande, non cumulable.</p>
    </div>
  );
};

export default PromoCodeInput; 